"use client";

import { Navbar } from "@/components/layout/navbar";
import { Footer } from "@/components/layout/footer";
import { CourseCard } from "@/components/course-card";
import type { CourseType } from "@/components/course-card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Filter, BookOpen, CalendarClock } from "lucide-react";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Link from "next/link";
import { listCoursesForCards, ACADEMY_INSTRUCTOR_LINE } from "@/lib/courses-catalog";
import { fetchPublicCourses } from "@/lib/catalog-api";
import { GST_PRICE_NOTE } from "@/lib/site-config";
import { getStudentUser, isStudent } from "@/lib/student-auth";
import { getBackendUrl } from "@/lib/backend-url";

// Map MongoDB courseId → static catalog slug
const MONGO_TO_SLUG: Record<string, string> = {
  "6a4555959d63313a981807ca": "360-data-science",
  "6a4555959d63313a981807cb": "data-analysts",
  "6a4555959d63313a981807cc": "python-ml",
  "6a4555959d63313a981807cd": "business-analyst",
  "6a4555959d63313a981807ce": "sas-data-analyst",
  "6a4555959d63313a981807cf": "tableau-viz",
  "6a4555959d63313a981807d0": "power-bi",
  "6a4555959d63313a981807d1": "sql-server",
  "6a4555959d63313a981807d2": "adv-excel",
};

const STATIC_COURSES = listCoursesForCards();

export default function CoursesPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState<string>("All");
  const [courses, setCourses] = useState<CourseType[]>(STATIC_COURSES);
  const [purchasedSlugs, setPurchasedSlugs] = useState<Set<string>>(new Set());

  // Load courses from API
  useEffect(() => {
    fetchPublicCourses().then((apiCourses) => {
      if (!apiCourses.length) return;
      const merged: CourseType[] = apiCourses.map((c) => {
        const staticMatch = STATIC_COURSES.find(
          (s) => s.id === c.slug || s.title.toLowerCase() === c.name.toLowerCase()
        );
        return {
          id:         c.slug || c.id,
          title:      c.name,
          instructor: ACADEMY_INSTRUCTOR_LINE,
          rating:     c.rating    || staticMatch?.rating    || 4.8,
          reviews:    c.reviews   || staticMatch?.reviews   || 0,
          price:      c.priceDisplay || staticMatch?.price  || "",
          level:      (c.level as CourseType["level"]) || staticMatch?.level || "All Levels",
          duration:   c.duration  || staticMatch?.duration  || "",
          image:      c.thumbnail || staticMatch?.image     || "",
        };
      });
      setCourses(merged);
    });
  }, []);

  // Check which courses the logged-in student has purchased
  useEffect(() => {
    const user = getStudentUser();
    if (!user || !isStudent(user)) return;

    const token = localStorage.getItem("mont2_admin_access_token");
    if (!token) return;

    // Fetch enrolled courses from the student's profile via the dashboard endpoint
    fetch(`${getBackendUrl()}/api/v1/dashboard/student`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: "no-store",
    })
      .then(r => r.ok ? r.json() : null)
      .then((data) => {
        if (!data) return;
        const progressCourses: any[] = data?.data?.progress?.courses ?? [];
        const slugs = new Set<string>();
        for (const c of progressCourses) {
          const courseId = c.courseId as string;
          if (courseId && MONGO_TO_SLUG[courseId]) {
            slugs.add(MONGO_TO_SLUG[courseId]);
          }
          // Also match by slug directly
          if (c.courseSlug) slugs.add(c.courseSlug as string);
        }
        setPurchasedSlugs(slugs);
      })
      .catch(() => {});
  }, []);

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = activeFilter === "All" || course.level === activeFilter;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="site-page min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 pt-28 pb-20">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10 rounded-2xl border border-border bg-card/40 backdrop-blur-md p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          >
            <div className="flex items-start gap-3">
              <div className="p-3 rounded-xl bg-mont-violet/15 text-mont-violet">
                <CalendarClock size={22} />
              </div>
              <div>
                <p className="text-sm font-semibold text-mont-violet uppercase tracking-wide">
                  Upcoming batches
                </p>
                <h2 className="text-xl font-bold font-heading text-foreground">
                  Batch schedules change weekly — confirm your seat on the dedicated page.
                </h2>
                <p className="text-sm text-muted-foreground mt-1">{GST_PRICE_NOTE}</p>
              </div>
            </div>
            <Link href="/upcoming-batch">
              <Button className="bg-gradient-primary text-white border-0 hover:opacity-90 whitespace-nowrap">
                View upcoming batch
              </Button>
            </Link>
          </motion.div>

          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-end gap-6 mb-12">
            <div>
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-3 mb-4 text-mont-violet"
              >
                <BookOpen size={24} />
                <span className="font-semibold tracking-wider uppercase text-sm">Course Catalog</span>
              </motion.div>
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-4xl md:text-5xl font-extrabold text-foreground"
              >
                Master Your Craft
              </motion.h1>
            </div>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-muted-foreground text-base sm:text-lg max-w-md md:text-right"
            >
              Choose from our selection of premium, industry-focused courses.
            </motion.p>
          </div>

          {/* Search and Filters */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col md:flex-row gap-4 mb-12 glass-card p-4 rounded-2xl"
          >
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground h-5 w-5" />
              <Input 
                type="text" 
                placeholder="Search courses by title, topic, or instructor..." 
                className="pl-12 bg-background border-border h-12 rounded-xl focus-visible:ring-mont-violet"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="flex items-center gap-2 overflow-x-auto py-3 -my-3 hide-scrollbar">
              <Button variant="outline" className="h-12 px-4 rounded-xl gap-2 flex-shrink-0 cursor-default hover:bg-background hover:text-foreground">
                <Filter size={18} /> Filters
              </Button>
              {["All", "Beginner", "Intermediate", "All Levels", "Beginner to Pro"].map((filter) => (
                <Button 
                  key={filter}
                  variant={activeFilter === filter ? "default" : "outline"}
                  className={`h-12 px-6 rounded-xl flex-shrink-0 transition-all duration-200 ${
                    activeFilter === filter 
                      ? "shadow-[0_0_15px_rgba(139,92,246,0.35)]" 
                      : "hover:border-mont-violet/50"
                  }`}
                  onClick={() => setActiveFilter(filter)}
                >
                  {filter}
                </Button>
              ))}
            </div>
          </motion.div>

          {/* Course Grid */}
          {filteredCourses.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredCourses.map((course, i) => (
                <CourseCard key={course.id} course={{ ...course, purchased: purchasedSlugs.has(course.id) }} index={i} />
              ))}
            </div>
          ) : (
            <div className="py-20 text-center flex flex-col items-center justify-center glass-card rounded-3xl border border-dashed border-border">
              <Search className="h-12 w-12 text-muted-foreground mb-4 opacity-50" />
              <h3 className="text-xl font-bold text-foreground mb-2">No courses found</h3>
              <p className="text-muted-foreground max-w-md">
                We couldn't find any courses matching "{searchTerm}" for the {activeFilter} level. Try adjusting your filters.
              </p>
              <Button 
                variant="outline" 
                className="mt-6 border-mont-violet text-mont-violet hover:bg-mont-violet/10"
                onClick={() => { setSearchTerm(""); setActiveFilter("All"); }}
              >
                Clear all filters
              </Button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
